/**
 * Composes the full FullCalendar events array for manual scheduling mode.
 * Merges: booked slot + external busy times + past background.
 *
 * Ref: KInterviewScheduler/.../useCalendarEvents.tsx
 */
import { useMemo } from 'react';
import moment from 'moment-timezone';
import type { EventInput } from '@fullcalendar/core';
import {
  EVENT_STYLES,
  EVENT_PRIORITY,
  BG_EVENT_CLASSES,
} from '../events/eventStyles';
import type { ScheduleSlot } from '../../details/useScheduleSharedState';

/** Busy blocks passed from `CalendarPanel` into FullCalendar via `useManualCalendarEvents`. */
export interface ManualCalendarExternalEvent {
  id?: string;
  start: string;
  end: string;
  title?: string;
  backgroundColor?: string;
  borderColor?: string;
  textColor?: string;
}

interface UseManualCalendarEventsOptions {
  bookedSlot: ScheduleSlot | null;
  externalEvents?: ManualCalendarExternalEvent[];
  timezone: string;
  interviewName?: string;
}

export function useManualCalendarEvents({
  bookedSlot,
  externalEvents = [],
  timezone,
  interviewName,
}: UseManualCalendarEventsOptions) {
  const events = useMemo((): EventInput[] => {
    const evts: EventInput[] = [];

    // 1. External events (interviewer busy times)
    for (const evt of externalEvents) {
      evts.push({
        id: evt.id ?? `ext-${evt.start}-${evt.end}`,
        start: evt.start,
        end: evt.end,
        title: evt.title ?? 'Busy',
        backgroundColor:
          evt.backgroundColor ?? EVENT_STYLES.busy.backgroundColor,
        borderColor: evt.borderColor ?? EVENT_STYLES.busy.borderColor,
        textColor: evt.textColor ?? EVENT_STYLES.busy.textColor,
        classNames: 'im-interviewer-busy',
        editable: false,
        extendedProps: {
          category: 'busy',
          priority: EVENT_PRIORITY.busy,
        },
      });
    }

    // 2. Booked slot — editable, draggable, resizable
    if (bookedSlot?.start && bookedSlot?.end) {
      evts.push({
        id: 'booked-slot',
        start: bookedSlot.start,
        end: bookedSlot.end,
        title: interviewName || 'Interview',
        backgroundColor: EVENT_STYLES.booked.backgroundColor,
        borderColor: EVENT_STYLES.booked.borderColor,
        textColor: EVENT_STYLES.booked.textColor,
        editable: true,
        durationEditable: true,
        startEditable: true,
        classNames: 'booked-event',
        extendedProps: {
          category: 'booked',
          priority: EVENT_PRIORITY.booked,
        },
      });
    }

    // 3. Past time background — grey overlay for all past times
    evts.push({
      start: moment()
        .tz(timezone)
        .subtract(10, 'year')
        .format('YYYY-MM-DDTHH:mm:ssZ'),
      end: moment().tz(timezone).format('YYYY-MM-DDTHH:mm:ssZ'),
      display: 'background',
      classNames: BG_EVENT_CLASSES.disabledSlots,
      color: 'transparent',
    });

    return evts;
  }, [bookedSlot, externalEvents, timezone, interviewName]);

  return { events };
}
